import { useCallback, useMemo } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { PhoneRecord } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { UsersQueries } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel, emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Avatar, Chat } from '@frontend/design-system';
import { ShareInMessageVirtuosoContext } from '../../types';

type CallBubbleProps = {
  callItem: PhoneRecord;
  context: ShareInMessageVirtuosoContext;
};

type BubbleInfo = {
  title: string;
  subtitle?: string;
  resultKey?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail' | 'robocall';
};

export const CallBubble = ({ callItem, context: { personInThreadContext, person, optedOut } }: CallBubbleProps) => {
  const isInbound = callItem.direction === Direction.DIRECTION_INBOUND;
  const isRobocall = callItem.entityType === EntityType.ENTITY_TYPE_ROBOCALL;

  const { t } = useTranslation('integrated-messaging');
  const { accessibleLocationIds } = useAppScopeStore();

  const users = UsersQueries.useGetMultiLocationsUsers({
    locationIds: accessibleLocationIds,
    params: { showChildren: true },
  }).flatMap(({ data }) => data || []);

  // personInThreadContext is for when thread body loads messages from different contact (custom number) while the header and sending context doesn't change.
  const inboundPatientName = personInThreadContext
    ? getFullName(personInThreadContext)
    : person
    ? getFullName(person)
    : undefined;

  const userSender = users.find((user) => user.UserID === callItem.createdBy);
  const userAvatarTitle = userSender ? getFullName(userSender) : undefined;

  // Robocall avatar: Auto Message with reminder icon
  const robocallAvatar = (
    <Avatar title={t('Auto Message')} name={t('Auto Message')} size='small' iconName='weave-logo' />
  );

  const avatar = isRobocall ? (
    robocallAvatar
  ) : isInbound ? (
    <Photos.ContactProfilePhoto
      title={!inboundPatientName ? t('Unknown User') : undefined}
      disableClick
      personId={personInThreadContext?.personId ?? person?.personId ?? callItem.personId ?? ''}
      locationId={callItem.locationId}
      name={inboundPatientName || undefined}
      size='small'
    />
  ) : (
    <Avatar title={userAvatarTitle ?? t('Unknown User')} name={userAvatarTitle} size='small' iconName='user' />
  );

  const timestamp = new Date(callItem.startedAt || '');
  const deleter = callItem.deletedAt ? users.find((user) => user.UserID === callItem.deletedBy) : undefined;
  const deletedBodyText = callItem.deletedAt
    ? t('This message was deleted on {{date}} by {{name}}', {
        date: new Date(callItem.deletedAt).toLocaleDateString(),
        name: deleter ? getFullName(deleter) : t('Unknown User'),
      })
    : undefined;

  const bubbleInfo: BubbleInfo = useMemo(() => {
    // Robocalls are always outbound auto-messages (appointment reminders)
    if (isRobocall) {
      return {
        title: t('Outbound Call'),
        subtitle: t('Appointment Reminder'),
        resultKey: 'robocall',
      };
    }

    const callResult = callItem.callResult;

    switch (callResult) {
      case CallResult.CALL_RESULT_ANSWERED:
        return isInbound
          ? {
              title: t('Inbound Call'),
              subtitle: inboundPatientName || 'Anonymous user',
              resultKey: 'inbound',
            }
          : { title: t('Outbound Call'), resultKey: 'outbound' };
      case CallResult.CALL_RESULT_ABANDONED:
        return { title: t('Abandoned Call'), resultKey: 'abandoned' };
      case CallResult.CALL_RESULT_MISSED:
        return { title: t('Missed Call'), resultKey: 'missed' };
      case CallResult.CALL_RESULT_VOICEMAIL:
        return { title: t('Voicemail'), resultKey: 'voicemail' };
      default:
        return { title: t('Unknown Call') };
    }
  }, [callItem.callResult, isRobocall, isInbound, inboundPatientName, t]);

  // Get the hover timestamp label for robocalls (includes voicemail distinction)
  const robocallSenderLabel = useMemo(() => {
    if (!isRobocall) return undefined;
    const isVoicemail = callItem.callResult === CallResult.CALL_RESULT_VOICEMAIL;
    return isVoicemail ? t('Auto Message Voicemail') : t('Auto Message');
  }, [isRobocall, callItem.callResult, t]);

  const robocallErrorMessage = useMemo(() => {
    if (!isRobocall) return undefined;
    switch (callItem.callResult) {
      case CallResult.CALL_RESULT_MISSED:
        return t('Not Delivered');
      case CallResult.CALL_RESULT_ERROR:
      case CallResult.CALL_RESULT_INVALID_DESTINATION:
        return t('Delivery Error');
      default:
        return undefined;
    }
  }, [isRobocall, callItem.callResult, t]);

  const isCallItemDeleted = callItem.deletedBy && callItem.deletedBy && deleter;

  // Robocalls don't have call insight or voicemail panels
  const handleCallAction = useCallback(() => {
    if (isRobocall) {
      return; // No action for robocalls
    }
    if (bubbleInfo.resultKey === 'voicemail') {
      emitOpenVoicemailInsightPanel({
        voicemailId: callItem.channelId,
        locationId: callItem.locationId,
      });
    } else {
      emitOpenCallInsightPanel({
        channelId: callItem.channelId,
        includeCallIntelligence: true,
        locationId: callItem.locationId,
      });
    }
  }, [
    emitOpenCallInsightPanel,
    emitOpenVoicemailInsightPanel,
    bubbleInfo.resultKey,
    callItem.channelId,
    callItem.locationId,
    isRobocall,
  ]);

  return (
    <Chat
      direction={isRobocall ? 'outbound' : isInbound ? 'inbound' : 'outbound'}
      avatar={avatar}
      timestamp={timestamp}
      error={robocallErrorMessage}
      senderName={isRobocall ? robocallSenderLabel : undefined}
      css={{
        padding: theme.spacing(0, 2),
        opacity: optedOut ? 0.5 : 1,
      }}
    >
      {isCallItemDeleted ? (
        <Chat.Bubble variant={'deleted'} text={deletedBodyText} />
      ) : (
        <Chat.Bubble
          variant='call'
          callResult={isRobocall ? 'outbound' : bubbleInfo.resultKey}
          callProps={{
            callResult: isRobocall ? 'outbound' : bubbleInfo.resultKey,
            callAction: isRobocall ? undefined : handleCallAction,
          }}
          text={bubbleInfo.title}
          subText={bubbleInfo.subtitle}
          key={callItem.channelId}
          maxWidth={202}
          styles={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.spacing(1),
            outline: `0.50px solid ${theme.colors.neutral20}`,
            '&:hover': {
              backgroundColor: theme.colors.neutral5,
            },
          }}
        />
      )}
    </Chat>
  );
};
